import React from "react"
import { useEffect, useRef, useState } from "react"
import { api } from "../api/client"
import { useLanguage } from "../context/LanguageContext"
import LoadingSpinner, { SkeletonCard } from "../components/LoadingSpinner"
import ParticleBackground from "../components/ParticleBackground"
import ProfileScene from "../components/ProfileScene"
import CountryPlace from "../components/CountryPlace"
import CountrySelect from "../components/CountrySelect"
import CountryFlag from "../components/CountryFlag"
import { countryName, localeForLanguage } from "../data/countries"

function formatMemberSince(iso) {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  } catch {
    return "—"
  }
}

export default function Profile() {
  const { t, language } = useLanguage()
  const [me, setMe] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loadError, setLoadError] = useState("")
  const [saveError, setSaveError] = useState("")
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState("")
  const [editing, setEditing] = useState(false)
  const shapesRef = useRef(null)
  const contentRef = useRef(null)

  const avatarOptions = [
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-1",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-2",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-3",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-4",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-5",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-6",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-7",
    "https://api.dicebear.com/9.x/bottts/svg?seed=cosmic-8",
  ]

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setLoadError("")
    Promise.all([api.me(), api.profile()])
      .then(([u, p]) => {
        if (mounted) {
          setMe(u)
          setProfile(p)
        }
      })
      .catch((e) => mounted && setLoadError(e.message))
      .finally(() => mounted && setLoading(false))
    return () => {
      mounted = false
    }
  }, [])

  useEffect(() => {
    const root = contentRef.current
    if (!root) return undefined
    const onFocusIn = () => setEditing(true)
    const onFocusOut = (e) => {
      if (!root.contains(e.relatedTarget)) setEditing(false)
    }
    root.addEventListener("focusin", onFocusIn)
    root.addEventListener("focusout", onFocusOut)
    return () => {
      root.removeEventListener("focusin", onFocusIn)
      root.removeEventListener("focusout", onFocusOut)
    }
  }, [loading, loadError, me, profile])

  const saveUser = async () => {
    setSaving(true)
    setSaveError("")
    setSaveSuccess("")
    try {
      const [updated, updatedProfile] = await Promise.all([
        api.updateMe({ first_name: me.first_name, last_name: me.last_name }),
        api.updateProfile({
          nationality: profile.nationality || null,
          current_location: profile.current_location || null,
        }),
      ])
      setMe(updated)
      setProfile(updatedProfile)
      setSaveSuccess(t("personalInfoSaved"))
      setTimeout(() => setSaveSuccess(""), 3000)
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const saveProfile = async () => {
    setSaving(true)
    setSaveError("")
    setSaveSuccess("")
    try {
      const updated = await api.updateProfile({
        bio: profile.bio,
        native_language: profile.native_language,
        english_level: profile.english_level,
        learning_goal: profile.learning_goal,
        daily_goal_minutes: profile.daily_goal_minutes,
        avatar_url: profile.avatar_url,
        nationality: profile.nationality || null,
        current_location: profile.current_location || null,
      })
      setProfile(updated)
      setSaveSuccess(t("learningProfileSaved"))
      setTimeout(() => setSaveSuccess(""), 3000)
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const sceneShell = (children) => (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <ParticleBackground particleCount={22} className="z-0 opacity-12 dark:opacity-8" />
      <ProfileScene layerRef={shapesRef} editing={editing || saving} />
      <div className="profile-ambient" aria-hidden />
      <div ref={contentRef} className="relative z-10 profile-scene">
        {children}
      </div>
    </div>
  )

  if (loading) {
    return sceneShell(
      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-8 lg:py-10 w-full min-w-0">
        <div className="animate-pulse space-y-6">
          <SkeletonCard className="h-28 sm:h-32 rounded-2xl" />
          <SkeletonCard className="h-40 sm:h-44 rounded-2xl" />
          <div className="grid gap-4 lg:grid-cols-2">
            <SkeletonCard className="h-56 sm:h-64 rounded-2xl" />
            <SkeletonCard className="h-56 sm:h-64 rounded-2xl" />
          </div>
          <SkeletonCard className="h-44 sm:h-48 rounded-2xl" />
        </div>
      </div>
    )
  }

  if (loadError) {
    return sceneShell(
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="profile-panel-3d max-w-md w-full rounded-2xl border border-destructive/30 bg-card/90 backdrop-blur-xl p-6 text-center shadow-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">{t("oopsSomethingWentWrong")}</p>
          <p className="text-sm text-destructive mb-6 break-words">{loadError}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="button-glow w-full py-2.5 rounded-xl bg-gradient-to-r from-primary to-accent text-primary-foreground text-sm font-medium hover:opacity-95 transition-opacity"
          >
            {t("tryAgain")}
          </button>
        </div>
      </div>
    )
  }

  if (!me || !profile) {
    return sceneShell(
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3 text-center">
          <LoadingSpinner text="" />
          <p className="text-sm text-muted-foreground">{t("loadingYourProfile")}</p>
        </div>
      </div>
    )
  }

  const displayName = [me.first_name, me.last_name].filter(Boolean).join(" ").trim() || me.username || me.email
  const progressPercentage = Math.min(((profile.daily_goal_minutes || 0) / 60) * 100, 100)

  return sceneShell(
    <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-8 lg:py-10 w-full min-w-0 pb-10">
      {saveSuccess && (
        <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-800 dark:text-emerald-200">
          {saveSuccess}
        </div>
      )}
      {saveError && (
        <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {saveError}
        </div>
      )}

      <header className="profile-hero-minimal mb-6 md:mb-8">
        <h1 className="profile-hero-title">{t("profile")}</h1>
        <div className="profile-hero-accent" aria-hidden />
      </header>

      {/* Identity hub */}
      <section className="profile-panel-3d profile-hub-card mb-8 overflow-hidden futuristic-nav-border">
        <div className="relative px-5 py-8 sm:px-8 sm:py-10">
          <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start sm:gap-8">
            <div className="profile-identity-photo">
              <div className="relative shrink-0">
                <div className="profile-avatar-portal h-28 w-28 sm:h-32 sm:w-32 bg-gradient-to-br from-primary/20 to-accent/10">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-3xl font-bold text-primary">
                      {(me.first_name?.[0] || me.username?.[0] || me.email?.[0] || "?").toUpperCase()}
                    </div>
                  )}
                </div>
                {profile.nationality && (
                  <CountryFlag
                    code={profile.nationality}
                    size={36}
                    className="country-flag--avatar"
                    label={countryName(profile.nationality, localeForLanguage(language))}
                  />
                )}
                {me.is_admin && (
                  <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-primary/40 bg-primary/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary sm:left-auto sm:right-0 sm:translate-x-0">
                    {t("adminBadge")}
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1 text-center sm:text-left">
              <h2 className="profile-identity-name">{displayName}</h2>
              <p className="profile-identity-meta">@{me.username} · {me.email}</p>
              <p className="mt-4 text-sm leading-relaxed text-foreground/90 max-w-2xl mx-auto sm:mx-0">
                {profile.bio || t("welcomeToJourney")}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                {profile.native_language && (
                  <span className="rounded-full border border-border bg-muted px-3 py-1 text-xs text-foreground">
                    {profile.native_language}
                  </span>
                )}
                {profile.english_level && (
                  <span className="rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                    {profile.english_level}
                  </span>
                )}
              </div>
            </div>
          </div>

          <dl className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3 border-t border-border/60 pt-6">
            <div className="profile-stat-chip px-4 py-3 text-center sm:text-left">
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("memberSince")}</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">{formatMemberSince(me.created_at)}</dd>
            </div>
            <div className="profile-stat-chip px-4 py-3 text-center sm:text-left">
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("accountStatus")}</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">
                {me.is_active ? t("accountStatusActive") : t("accountStatusInactive")}
              </dd>
            </div>
            <div className="profile-stat-chip px-4 py-3 text-center sm:text-left">
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{t("currentLocation")}</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">
                {profile.current_location ? <CountryPlace code={profile.current_location} /> : "—"}
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-2 lg:gap-8">
        <section className="profile-panel-3d profile-section-card p-5 sm:p-6 futuristic-nav-border">
          <h2 className="profile-section-title">{t("personalInformation")}</h2>
          <p className="profile-section-desc mb-6">{t("profilePersonalInfoHint")}</p>
          <div className="space-y-4">
            <div className="profile-field">
              <label htmlFor="profile-first-name">{t("firstName")}</label>
              <input
                id="profile-first-name"
                type="text"
                value={me.first_name || ""}
                onChange={(e) => setMe({ ...me, first_name: e.target.value })}
                className="profile-input"
                placeholder={t("enterFirstName")}
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-last-name">{t("lastName")}</label>
              <input
                id="profile-last-name"
                type="text"
                value={me.last_name || ""}
                onChange={(e) => setMe({ ...me, last_name: e.target.value })}
                className="profile-input"
                placeholder={t("enterLastName")}
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-nationality">{t("nationality")}</label>
              <CountrySelect
                id="profile-nationality"
                value={profile.nationality || ""}
                onChange={(code) => setProfile({ ...profile, nationality: code })}
                placeholder={t("selectCountry")}
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-location">{t("currentLocation")}</label>
              <CountrySelect
                id="profile-location"
                value={profile.current_location || ""}
                onChange={(code) => setProfile({ ...profile, current_location: code })}
                placeholder={t("selectCountry")}
              />
            </div>
            <div className="profile-readonly-note">
              <p>{t("profileAccountReadOnlyNote")}</p>
              <p className="font-mono text-[11px]">{me.email}</p>
              <p className="font-mono text-[11px]">@{me.username}</p>
            </div>
            <button
              type="button"
              onClick={saveUser}
              disabled={saving}
              className="button-glow w-full rounded-xl bg-gradient-to-r from-primary to-accent py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-opacity hover:opacity-95 disabled:opacity-50"
            >
              {saving ? t("saving") : t("savePersonalInformation")}
            </button>
          </div>
        </section>

        <section className="profile-panel-3d profile-section-card p-5 sm:p-6 futuristic-nav-border">
          <h2 className="profile-section-title">{t("learningProgress")}</h2>
          <p className="profile-section-desc mb-6">{t("profileLearningProgressHint")}</p>
          <div className="space-y-4">
            <div className="profile-goal-highlight">
              <p className="text-xs uppercase tracking-wider text-primary/80 mb-1">{t("learningGoal")}</p>
              <p className="text-sm text-foreground">{profile.learning_goal || t("setYourLearningObjective")}</p>
            </div>
            <div>
              <div className="mb-2 flex justify-between text-sm">
                <span className="text-muted-foreground">{t("dailyGoal")}</span>
                <span className="font-medium text-foreground tabular-nums">
                  {profile.daily_goal_minutes || 0} {t("minutes")}
                </span>
              </div>
              <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
              <p className="mt-1.5 text-right text-xs text-muted-foreground tabular-nums">
                {Math.round(progressPercentage)}% {t("ofDailyGoal")}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="profile-stat-chip p-4 text-center">
                <p className="text-2xl font-bold text-primary tabular-nums">{profile.daily_goal_minutes || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("dailyMinutes")}</p>
              </div>
              <div className="profile-stat-chip p-4 text-center">
                <p className="text-lg font-bold text-foreground">{profile.english_level || "—"}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("englishLevel")}</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="profile-panel-3d profile-section-card mt-8 p-5 sm:p-6 futuristic-nav-border w-full min-w-0">
        <h2 className="profile-section-title">{t("learningProfile")}</h2>
        <p className="profile-section-desc mb-6">{t("profileLearningProfileHint")}</p>

        <div className="space-y-5">
          <div className="profile-field">
            <label htmlFor="profile-bio">{t("bio")}</label>
            <textarea
              id="profile-bio"
              value={profile.bio || ""}
              onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
              className="profile-input min-h-[100px] resize-y"
              rows={3}
              placeholder={t("tellUsAboutJourney")}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="profile-field">
              <label htmlFor="profile-native-language">{t("nativeLanguage")}</label>
              <input
                id="profile-native-language"
                type="text"
                value={profile.native_language || ""}
                onChange={(e) => setProfile({ ...profile, native_language: e.target.value })}
                className="profile-input"
                placeholder={t("egSpanish")}
              />
            </div>
            <div className="profile-field">
              <label htmlFor="profile-english-level">{t("englishLevel")}</label>
              <select
                id="profile-english-level"
                value={profile.english_level || ""}
                onChange={(e) => setProfile({ ...profile, english_level: e.target.value })}
                className="profile-input"
              >
                <option value="">{t("selectLevel")}</option>
                <option value="Beginner">{t("beginner")}</option>
                <option value="Intermediate">{t("intermediate")}</option>
                <option value="Advanced">{t("advanced")}</option>
              </select>
            </div>
          </div>
          <div className="profile-field">
            <label htmlFor="profile-learning-goal">{t("learningGoal")}</label>
            <input
              id="profile-learning-goal"
              type="text"
              value={profile.learning_goal || ""}
              onChange={(e) => setProfile({ ...profile, learning_goal: e.target.value })}
              className="profile-input"
              placeholder={t("egPassTOEFL")}
            />
          </div>
          <div className="profile-field sm:max-w-xs">
            <label htmlFor="profile-daily-goal">
              {t("dailyGoal")} <span className="font-normal text-muted-foreground">({t("minutes")})</span>
            </label>
            <input
              id="profile-daily-goal"
              type="number"
              value={profile.daily_goal_minutes ?? 0}
              onChange={(e) => setProfile({ ...profile, daily_goal_minutes: Number(e.target.value) })}
              className="profile-input"
              min={0}
              max={480}
            />
          </div>

          <div className="profile-field">
            <span className="text-sm font-semibold text-foreground">{t("profilePicture")}</span>
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 sm:gap-3">
              {avatarOptions.map((url) => {
                const selected = profile.avatar_url === url
                return (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setProfile({ ...profile, avatar_url: url })}
                    className={`profile-avatar-pick relative overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                      selected ? "profile-avatar-pick--selected" : "hover:border-primary/40"
                    }`}
                  >
                    <img src={url} alt="" className="h-14 w-full object-cover bg-muted sm:h-16" />
                  </button>
                )
              })}
            </div>
            <button
              type="button"
              onClick={() => setProfile({ ...profile, avatar_url: "" })}
              className="self-start rounded-lg border border-border px-3 py-2 text-xs text-muted-foreground hover:bg-muted transition-colors"
            >
              {t("removePhoto")}
            </button>
          </div>

          <button
            type="button"
            onClick={saveProfile}
            disabled={saving}
            className="button-glow w-full rounded-xl bg-gradient-to-r from-primary to-accent py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-opacity hover:opacity-95 disabled:opacity-50"
          >
            {saving ? t("saving") : t("saveLearningProfile")}
          </button>
        </div>
      </section>
    </div>
  )
}
